import { useMemo, useState } from "react";
import { useSeo } from "@/hooks/useSeo";
import { characters } from "@/mocks/characters";
import { works } from "@/mocks/works";
import CharacterCard from "@/components/feature/CharacterCard";
import Breadcrumbs from "@/components/feature/Breadcrumbs";

export default function CharactersList() {
  useSeo({
    title: "希岛爱理角色解析专题 - 角色背景与代表台词",
    description:
      "希岛爱理角色解析专题，按作品整理其扮演的各个角色，介绍角色背景、代表台词与剧情片段，帮助影迷深入理解每一个角色。",
    keywords: "希岛爱理角色,角色解析,角色背景,代表台词,影视角色",
  });

  const [workFilter, setWorkFilter] = useState("全部");

  const workOptions = useMemo(() => {
    const ids = Array.from(new Set(characters.map((c) => c.workId)));
    return ids.map((id) => works.find((w) => w.id === id)).filter(Boolean);
  }, []);

  const filtered = useMemo(() => {
    if (workFilter === "全部") return characters;
    const target = works.find((w) => w.title === workFilter);
    return characters.filter((c) => c.workId === target?.id);
  }, [workFilter]);

  return (
    <div className="w-full pb-16">
      <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "角色解析" }]} />

      <div className="w-full px-4 md:px-8 pt-6">
        <div className="rounded-lg bg-primary-950 text-background-100 p-6 md:p-10">
          <span className="text-xs font-label font-semibold tracking-[0.2em] uppercase text-accent-400">
            Character Analysis
          </span>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mt-2">
            角色解析专题
          </h1>
          <p className="text-sm text-background-300 mt-3 max-w-3xl leading-relaxed">
            每一个角色都值得被认真对待。本专题按作品归类希岛爱理饰演的角色，从背景设定、代表台词到关键剧情片段，带你走近角色本身。
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 pt-8">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm text-foreground-500 whitespace-nowrap inline-flex items-center gap-1.5">
            <i className="ri-filter-3-line text-accent-600" />
            按作品筛选
          </span>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setWorkFilter("全部")}
              className={`px-3.5 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                workFilter === "全部"
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 border border-background-200 text-foreground-700 hover:border-primary-300"
              }`}
            >
              全部
            </button>
            {workOptions.map(
              (w) =>
                w && (
                  <button
                    key={w.id}
                    type="button"
                    onClick={() => setWorkFilter(w.title)}
                    className={`px-3.5 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      workFilter === w.title
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 border border-background-200 text-foreground-700 hover:border-primary-300"
                    }`}
                  >
                    《{w.title}》
                  </button>
                )
            )}
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 pt-6">
        <p className="text-sm text-foreground-600 mb-6">
          共 <span className="font-semibold text-accent-600">{filtered.length}</span> 个角色
        </p>
        {filtered.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {filtered.map((c) => (
              <CharacterCard key={c.id} character={c} />
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-100 py-20 text-center">
            <i className="ri-user-search-line text-4xl text-foreground-300" />
            <p className="mt-3 text-foreground-700">该作品下暂无角色解析</p>
            <button
              type="button"
              onClick={() => setWorkFilter("全部")}
              className="mt-5 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line" />
              查看全部角色
            </button>
          </div>
        )}
      </div>
    </div>
  );
}